import React, { useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Brain, ExternalLink, FileText } from 'lucide-react';
import { AGENT_ACTION_TYPES } from '../constants/actionTypes';
import AIAgentPlanCard from './AIAgentPlanCard';
import AIAgentThinkingSteps from './AIAgentThinkingSteps';
import AIAgentRecentSessions from './AIAgentRecentSessions';
import AIAgentSmartInbox from './AIAgentSmartInbox';
import AIAgentRecentDocuments from './AIAgentRecentDocuments';
import AIAgentPaymentsPanel from './AIAgentPaymentsPanel';
import AIAgentGenericPanel from './AIAgentGenericPanel';
import AIAgentDocumentDraftCard from './AIAgentDocumentDraftCard';
import AIAgentDecisionPackage from './AIAgentDecisionPackage';
import { tabP } from '../config/agentModuleI18n';

const BUBBLE_STYLES = {
  prompt:
    'bg-blue-500/20 border-blue-400/30 text-blue-50',
  result:
    'bg-white/10 border-white/20 backdrop-blur-lg text-white/95',
  clarification:
    'bg-amber-400/10 border-amber-300/25 backdrop-blur-lg text-amber-50',
  plan:
    'bg-white/10 border-white/20 backdrop-blur-lg text-white/95',
  error:
    'bg-red-500/10 border-red-400/30 backdrop-blur-lg text-red-100',
  warn:
    'bg-amber-400/10 border-amber-300/25 backdrop-blur-lg text-amber-50',
};

const BUBBLE_STYLES_LIGHT = {
  prompt: 'bg-blue-50 border-blue-200 text-blue-900',
  result: 'bg-white border-gray-200 text-gray-800',
  clarification: 'bg-amber-50 border-amber-200 text-amber-900',
  plan: 'bg-white border-gray-200 text-gray-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warn: 'bg-amber-50 border-amber-200 text-amber-900',
};

const EMPTY_STATE_ACTION_KEYS = [
  'documents',
  'clients_catalog',
  'payments',
  'analytics',
];

export default function AIAgentMessageThread({
  entries,
  pendingConfirmation,
  pendingPlan,
  isBuildingPlan = false,
  isProcessing,
  agentMode = 'agent',
  licenseBlocked,
  onConfirm,
  onCancel,
  onBuildPlan,
  onCancelPlan,
  variant = 'compact',
  lightSurface = false,
  onQuickPrompt,
  emptyStateActions,
  emptyStateHint,
  recentSessions,
  activeSessionId,
  onOpenSession,
  onToggleSessionShare,
  onDeleteSession,
  inboxItems,
  isInboxLoading,
  onInboxTreat,
  sidePanel = null,
  panels = {},
  recentDocuments,
  paymentGroups,
  isPanelsLoading,
  onPanelTreat,
  onDocumentTreat,
  onPaymentTreat,
  onOpenDocumentHandoff,
  onOpenDocumentEditor,
  onDraftQuickAction,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.messageThread.${key}`, opts);
  const scrollRef = useRef(null);
  const isWorkspace = variant === 'workspace';

  const defaultEmptyStateActions = useMemo(
    () =>
      EMPTY_STATE_ACTION_KEYS.map((actionKey) => ({
        title: p(`empty_actions.${actionKey}.title`),
        desc: p(`empty_actions.${actionKey}.desc`),
        prompt: p(`empty_actions.${actionKey}.prompt`),
      })),
    [t],
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, pendingConfirmation, pendingPlan, isProcessing]);

  const entryLabel = (kind) => {
    if (kind === 'prompt') return p('entry_you');
    if (kind === 'clarification') return p('entry_clarification');
    if (kind === 'plan') return p('entry_plan');
    if (kind === 'error') return p('entry_error');
    return p('entry_agent');
  };

  const isDocumentHandoffAction =
    pendingConfirmation?.action?.type === AGENT_ACTION_TYPES.CREATE_INVOICE;

  const openDocumentEditor = onOpenDocumentEditor || onOpenDocumentHandoff;
  const treatItem = onPanelTreat || onDocumentTreat || onPaymentTreat;

  const activePanel = sidePanel ? panels[sidePanel] : null;

  const renderWorkspacePanel = () => {
    if (!isWorkspace || !sidePanel) return null;

    if (sidePanel === 'inbox') {
      return (
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 pt-2">
          <AIAgentSmartInbox
            items={inboxItems}
            isLoading={isInboxLoading}
            onTreatItem={onInboxTreat}
            showWhenEmpty
          />
        </div>
      );
    }

    if (sidePanel === 'sessions') {
      return (
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 pt-2">
          <AIAgentRecentSessions
            sessions={recentSessions}
            activeSessionId={activeSessionId}
            onOpenSession={onOpenSession}
            onToggleSessionShare={onToggleSessionShare}
            onDeleteSession={onDeleteSession}
            showWhenEmpty
          />
        </div>
      );
    }

    if (sidePanel === 'documents') {
      const docItems = activePanel?.type === 'documents' && activePanel.items?.[0]?.number
        ? activePanel.items
        : recentDocuments;

      return (
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 pt-2">
          <AIAgentRecentDocuments
            documents={docItems}
            isLoading={isPanelsLoading}
            onTreatDocument={treatItem}
            showWhenEmpty
          />
        </div>
      );
    }

    if (sidePanel === 'payments') {
      const paymentPanel = activePanel?.type === 'payments' ? activePanel : null;
      return (
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 pt-2">
          <AIAgentPaymentsPanel
            groups={paymentPanel?.groups || paymentGroups}
            isLoading={isPanelsLoading}
            onTreatPayment={onPaymentTreat || treatItem}
            showWhenEmpty
          />
        </div>
      );
    }

    if (activePanel?.type === 'list') {
      return (
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 pt-2">
          <AIAgentGenericPanel
            title={activePanel.title}
            items={activePanel.items}
            isLoading={isPanelsLoading}
            onTreatItem={treatItem}
            emptyMessage={activePanel.emptyMessage}
            showWhenEmpty
          />
        </div>
      );
    }

    return (
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 pt-2">
        <AIAgentGenericPanel
          title={tabP(sidePanel, { defaultValue: p('panel_unknown') })}
          items={[]}
          isLoading={isPanelsLoading}
          onTreatItem={treatItem}
          emptyMessage={p('panel_empty')}
          showWhenEmpty
        />
      </div>
    );
  };

  const renderDocumentHandoffButton = (openPath, draft) => {
    if (!openPath || !openDocumentEditor) return null;

    return (
      <button
        type="button"
        onClick={() => openDocumentEditor(draft || { openPath })}
        className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
          lightSurface
            ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
            : 'border-blue-400/40 bg-blue-500/20 text-blue-100 hover:bg-blue-500/30'
        }`}
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {p('show_form')}
      </button>
    );
  };

  return (
    <div
      ref={scrollRef}
      className={
        isWorkspace
          ? 'h-full overflow-y-auto flex flex-col gap-3 pr-1'
          : 'overflow-y-auto px-3 py-2.5 max-h-[min(36vh,280px)] max-[700px]:max-h-[22vh]'
      }
    >
      {licenseBlocked && (
        <p
          className={`text-xs mb-2${
            isWorkspace
              ? ` max-w-[min(760px,88%)] p-3.5 rounded-2xl border ${
                  lightSurface ? BUBBLE_STYLES_LIGHT.warn : BUBBLE_STYLES.warn
                } ${lightSurface ? '' : ' text-amber-200'}`
              : ' text-amber-200'
          }`}
        >
          {p('license_online_required')}
        </p>
      )}

      {renderWorkspacePanel()}

      {!entries.length && !licenseBlocked && isWorkspace && (
        <div className="mx-auto w-full max-w-4xl py-8 px-4 sm:px-6 my-auto">
          <div className="max-w-xl mx-auto text-center">
            <div className="relative group inline-block mb-6">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl opacity-40 group-hover:opacity-60 transition-opacity duration-500 blur-md" />
              <div className="relative p-5 rounded-3xl bg-white/10 border border-white/20 backdrop-blur-lg">
                <Brain className="h-10 w-10 text-blue-400 mx-auto" />
              </div>
            </div>
            <p className={`m-0 mb-2 text-2xl font-bold ${lightSurface ? 'text-gray-900' : 'text-white'}`}>
              {p('agent_title')}
            </p>
            <p className={`m-0 leading-relaxed text-sm max-w-md mx-auto ${lightSurface ? 'text-gray-500' : 'text-white/60'}`}>
              {emptyStateHint || p('empty_hint')}
            </p>
          </div>

          <div className="max-w-xl mx-auto text-center">
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              {(emptyStateActions || defaultEmptyStateActions).map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => onQuickPrompt?.(item.prompt)}
                  className={`p-4 rounded-2xl border text-left cursor-pointer ${
                    lightSurface
                      ? 'bg-gray-50 border-gray-200 hover:bg-blue-50 hover:border-blue-200'
                      : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/[0.07] transition-all'
                  }`}
                >
                  <p className={`m-0 font-semibold text-sm ${lightSurface ? 'text-gray-900' : 'text-white'}`}>
                    {item.title}
                  </p>
                  <p className={`m-0 mt-1 text-xs ${lightSurface ? 'text-gray-500' : 'text-white/50'}`}>
                    {item.desc}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {entries.map((entry) => {
        if (entry.kind === 'plan' && pendingPlan) return null;

        const bubbleStyles = lightSurface ? BUBBLE_STYLES_LIGHT : BUBBLE_STYLES;
        const bubbleStyle = bubbleStyles[entry.kind] || bubbleStyles.result;

        if (isWorkspace) {
          return (
            <div
              key={entry.id}
              className={`flex w-full ${
                entry.kind === 'prompt' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[min(760px,88%)] px-4 py-3 rounded-2xl border ${bubbleStyle}`}
              >
                <span
                  className={`block text-[0.68rem] font-semibold tracking-wide uppercase mb-1 ${
                    entry.kind === 'prompt' ? 'text-blue-200/70' : 'text-white/50'
                  }`}
                >
                  {entryLabel(entry.kind)}
                </span>
                <p className="m-0 leading-relaxed whitespace-pre-wrap break-words">
                  {entry.text}
                </p>
                {entry.kind === 'document-draft' && entry.documentDraft ? (
                  <AIAgentDocumentDraftCard
                    draft={entry.documentDraft}
                    lightSurface={lightSurface}
                    onOpenEditor={openDocumentEditor}
                    onQuickAction={onDraftQuickAction}
                  />
                ) : null}
                {entry.imagePreviewUrl ? (
                  <img
                    src={entry.imagePreviewUrl}
                    alt={entry.imageName || p('attached_document')}
                    className="mt-2 max-w-full max-h-48 rounded-xl border border-white/15 object-contain"
                  />
                ) : entry.attachmentKind === 'pdf' ? (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/15 bg-white/5 text-white/80 text-sm">
                    <FileText size={18} className="text-red-300 shrink-0" />
                    <span className="truncate">{entry.imageName || p('pdf_document')}</span>
                  </div>
                ) : null}
                {entry.executionResult?.success &&
                entry.executionResult?.message &&
                entry.kind !== 'document-draft' ? (
                  <span className={`block mt-1 text-xs ${lightSurface ? 'text-emerald-600' : 'text-emerald-300'}`}>
                    {entry.executionResult.message}
                  </span>
                ) : null}
                {entry.kind !== 'document-draft' &&
                  renderDocumentHandoffButton(
                    entry.executionResult?.openPath,
                    entry.documentDraft || entry.executionResult?.conversionData
                      ? {
                          openPath: entry.executionResult?.openPath,
                          conversionData: entry.executionResult?.conversionData,
                        }
                      : null,
                  )}
              </div>
            </div>
          );
        }

        return (
          <div key={entry.id} className="mb-3 last:mb-0">
            {entry.kind === 'prompt' && (
              <span className="block text-[0.62rem] font-semibold tracking-widest uppercase text-blue-200/70 mb-0.5">
                {p('entry_you')}
              </span>
            )}
            {entry.kind === 'result' && (
              <span className="block text-[0.62rem] font-semibold tracking-widest uppercase text-white/45 mb-0.5">
                {p('entry_result')}
              </span>
            )}
            {entry.kind === 'clarification' && (
              <span className="block text-[0.62rem] font-semibold tracking-widest uppercase text-amber-200/70 mb-0.5">
                {p('entry_clarification')}
              </span>
            )}
            {entry.kind === 'plan' && (
              <span className="block text-[0.62rem] font-semibold tracking-widest uppercase text-white/45 mb-0.5">
                {p('entry_plan')}
              </span>
            )}
            {entry.kind === 'error' && (
              <span className="block text-[0.62rem] font-semibold tracking-widest uppercase text-red-300/80 mb-0.5">
                {p('entry_error')}
              </span>
            )}
            <p
              className={`m-0 text-sm leading-normal whitespace-pre-wrap break-words ${
                entry.kind === 'error'
                  ? 'text-red-200'
                  : entry.kind === 'prompt'
                    ? 'text-blue-50'
                    : 'text-white/90'
              }`}
            >
              {entry.text}
            </p>
            {entry.kind === 'document-draft' && entry.documentDraft ? (
              <AIAgentDocumentDraftCard
                draft={entry.documentDraft}
                lightSurface={lightSurface}
                onOpenEditor={openDocumentEditor}
                onQuickAction={onDraftQuickAction}
              />
            ) : null}
            {entry.imagePreviewUrl ? (
              <img
                src={entry.imagePreviewUrl}
                alt={entry.imageName || p('attached_document')}
                className="mt-2 max-w-full max-h-40 rounded-lg border border-white/15 object-contain"
              />
            ) : entry.attachmentKind === 'pdf' ? (
              <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-white/15 bg-white/5 text-white/80 text-xs">
                <FileText size={16} className="text-red-300 shrink-0" />
                <span className="truncate">{entry.imageName || p('pdf_document')}</span>
              </div>
            ) : null}
            {entry.executionResult?.success &&
            entry.executionResult?.message &&
            entry.kind !== 'document-draft' ? (
              <span className="block mt-1 text-xs text-emerald-300">
                {entry.executionResult.message}
              </span>
            ) : null}
            {entry.kind !== 'document-draft' &&
              renderDocumentHandoffButton(entry.executionResult?.openPath)}
          </div>
        );
      })}

      {pendingPlan && (
        <div className={isWorkspace ? 'flex justify-start w-full' : ''}>
          <AIAgentPlanCard
            plan={pendingPlan}
            onBuild={onBuildPlan}
            onCancel={onCancelPlan}
            lightSurface={lightSurface}
            isBuilding={isBuildingPlan}
          />
        </div>
      )}

      {pendingConfirmation && (
        <div className={isWorkspace ? 'flex justify-start w-full' : ''}>
          <div
            className={
              isWorkspace
                ? lightSurface
                  ? 'max-w-[min(760px,88%)] p-4 rounded-2xl border border-dashed border-blue-300 bg-blue-50'
                  : 'max-w-[min(760px,88%)] p-4 rounded-2xl border border-dashed border-white/25 bg-white/10 backdrop-blur-lg'
                : 'p-2.5 rounded-lg border border-white/20 bg-white/[0.06] backdrop-blur-md'
            }
          >
            {isWorkspace && (
              <span
                className={`block text-[0.68rem] font-semibold tracking-wide uppercase mb-1 ${
                  lightSurface ? 'text-gray-500' : 'text-white/50'
                }`}
              >
                {p('entry_agent')}
              </span>
            )}
            {pendingConfirmation.decisionPackage ? (
              <AIAgentDecisionPackage
                package={pendingConfirmation.decisionPackage}
                onConfirm={onConfirm}
                onCancel={onCancel}
                isProcessing={isProcessing}
                lightSurface={lightSurface}
              />
            ) : (
              <>
            <p
              className={`m-0 mb-3 whitespace-pre-wrap ${
                isWorkspace
                  ? lightSurface
                    ? 'text-blue-900'
                    : 'text-white/90'
                  : 'text-white/85 text-sm'
              }`}
            >
              {pendingConfirmation.message}
            </p>
            <div className="flex gap-1.5">
              <button
                type="button"
                className="px-2.5 py-1.5 rounded-md border-none text-xs cursor-pointer bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                onClick={onConfirm}
              >
                {isDocumentHandoffAction ? p('prepare_draft') : p('confirm')}
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded-md border text-xs cursor-pointer ${
                  isWorkspace && lightSurface
                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    : 'border-white/20 bg-white/10 text-white/80 hover:bg-white/15'
                }`}
                onClick={onCancel}
              >
                {p('cancel')}
              </button>
            </div>
              </>
            )}
          </div>
        </div>
      )}

      {isProcessing && (
        <AIAgentThinkingSteps
          isActive={isProcessing}
          agentMode={agentMode}
          isBuildingPlan={isBuildingPlan}
          lightSurface={lightSurface}
          variant={variant}
        />
      )}
    </div>
  );
}
